/**
 * Retail subscription UI for PDP. WeTheme's wetheme-product-form (Vue 2) replaces / patches
 * markup after mount; any closed-over DOM refs from the first paint can point at detached
 * nodes. All sync paths MUST resolve #AddToCartForm and .subscription-plans-by-customer on
 * each run; call clnfInitSubscriptionPlansByCustomer after Vue is ready.
 */
(function() {
  var mo = null;

  /**
   * WeTheme PDP: variant is driven by select.product-single__variants (v-model). Prefer that over any
   * hidden input[name=id] so we do not read a stale id before/after Vue patches the tree.
   */
  function findVariantControl(root) {
    if (!root) return null;
    var sel =
      root.querySelector("select.product-single__variants") ||
      root.querySelector('select[name="id"]');
    if (sel) return sel;
    return root.querySelector('input[name="id"]');
  }

  /** Shopify / Vue sometimes pass string ids; dataset parses to number — never use !== without this. */
  function coerceVariantId(raw) {
    if (raw == null || raw === '') return null;
    var n = Number(raw);
    return isNaN(n) ? null : n;
  }

  /**
   * Vue root: physical <wetheme-product-form> when present, else WeTheme mount on .product-form-vue
   * (inline-template compiles away the custom element).
   */
  function getVueProductFormRoot() {
    var wf = document.querySelector('wetheme-product-form');
    if (wf && wf.__vue__) return wf;
    var mount = document.querySelector('.product-form-vue');
    if (mount && mount.__vue__) return mount;
    return null;
  }

  /**
   * Swatch mode updates wetheme-product-form’s Vue state before/without select.value catching up.
   * Prefer the component’s variant id when the form is marked ready (product-custom.js adds this class).
   */
  function getVariantIdFromVue() {
    var el = getVueProductFormRoot();
    if (!el || !el.__vue__) return null;
    function tryVm(v) {
      if (!v) return null;
      var variant = v.variant;
      if (!variant && v.$data) variant = v.$data.variant;
      if (!variant && typeof v._data !== 'undefined') variant = v._data.variant;
      if (variant && typeof variant === 'object' && variant.id != null) {
        return coerceVariantId(variant.id);
      }
      return null;
    }
    function walk(vm) {
      if (!vm) return null;
      var got = tryVm(vm);
      if (got != null) return got;
      var ch = vm.$children;
      if (!ch || !ch.length) return null;
      for (var i = 0; i < ch.length; i++) {
        got = walk(ch[i]);
        if (got != null) return got;
      }
      return null;
    }
    return walk(el.__vue__);
  }

  function fieldsetSubscriptionInactive(fieldset) {
    if (!fieldset) return true;
    if (fieldset.hidden === true) return true;
    if (fieldset.getAttribute('hidden') !== null && fieldset.getAttribute('hidden') !== 'false') return true;
    return fieldset.classList.contains('subscription-plans-by-customer--hidden');
  }

  /** Always the live form + snippet root (never cache across ticks after Vue mount). */
  function getLiveRefs() {
    var form = document.getElementById('AddToCartForm');
    if (!form) {
      return { form: null, container: null, variantInput: null, sellingPlanInput: null };
    }
    var container = form.querySelector('.subscription-plans-by-customer');
    var variantInput = findVariantControl(form);
    var sellingPlanInput = container ? container.querySelector('.selected-selling-plan-id') : null;
    return { form: form, container: container, variantInput: variantInput, sellingPlanInput: sellingPlanInput };
  }

  function variantIdFromUrl() {
    try {
      var u = new URL(window.location.href);
      var v = u.searchParams.get('variant');
      if (v) return parseInt(v, 10);
    } catch (e) {}
    return null;
  }

  function productFormVueReady() {
    var wf = document.querySelector('wetheme-product-form');
    var mount = document.querySelector('.product-form-vue');
    return !!(
      (wf && wf.classList.contains('product-form--ready')) ||
      (mount && mount.classList.contains('product-form--ready'))
    );
  }

  function getCurrentVariantId(refs) {
    refs = refs || getLiveRefs();
    if (productFormVueReady()) {
      var fromVue = getVariantIdFromVue();
      if (fromVue != null) return fromVue;
    }
    var val = refs.variantInput && refs.variantInput.value;
    var fromControl = coerceVariantId(val);
    if (fromControl != null) return fromControl;
    return coerceVariantId(variantIdFromUrl());
  }

  var _qsvpRafIds = [];
  var _qsvpTimerIds = [];

  /** Swatch / Vue: select value can lag; run sync now and on later frames (matches product-custom reinit pattern). */
  function queueShowVariantPlans() {
    var run = function() {
      showVariantPlans(getCurrentVariantId(), getLiveRefs());
      scheduleSyncSellingPlan();
    };
    _qsvpRafIds.splice(0).forEach(cancelAnimationFrame);
    _qsvpTimerIds.splice(0).forEach(clearTimeout);
    run();
    try {
      _qsvpRafIds.push(requestAnimationFrame(function() {
        run();
        _qsvpRafIds.push(requestAnimationFrame(run));
      }));
    } catch (eRafq) {}
    [0, 35, 100, 220].forEach(function(ms) {
      _qsvpTimerIds.push(setTimeout(run, ms));
    });
  }

  function showVariantPlans(variantId, refs) {
    refs = refs || getLiveRefs();
    if (!refs.container) return;
    var vid = coerceVariantId(variantId);
    if (vid == null) {
      vid = getCurrentVariantId(refs);
    }
    var hint = refs.container.querySelector('.subscription-plans-by-customer__hint-below');
    if (vid == null) {
      if (hint) {
        hint.classList.add('subscription-plans-by-customer--hidden');
        hint.setAttribute('aria-hidden', 'true');
        hint.hidden = true;
      }
      return;
    }
    refs.container.querySelectorAll('.subscription-plans-fieldset').forEach(function(fieldset) {
      var rawId = fieldset.getAttribute('data-variant-id');
      var fid = coerceVariantId(rawId);
      if (fid == null) {
        return;
      }
      var hide = fid !== vid;
      fieldset.classList.toggle('subscription-plans-by-customer--hidden', hide);
      fieldset.hidden = hide;
      if (hide) {
        fieldset.setAttribute('aria-hidden', 'true');
      } else {
        fieldset.removeAttribute('aria-hidden');
      }
    });
    if (hint) {
      var fsForVariant = refs.container.querySelector(
        '.subscription-plans-fieldset[data-variant-id="' + String(vid) + '"]'
      );
      /**
       * Only nudge Subscribe after a subscription plan is chosen. Showing this hint while
       * "One-time purchase" is selected read as "you cannot use Add to cart" on mixed PDPs where
       * only some variants have plans (e.g. Black Turtle Beans 50 lb vs 5 lb).
       */
      var showHint = false;
      if (
        fsForVariant &&
        !fieldsetSubscriptionInactive(fsForVariant)
      ) {
        var cr = fsForVariant.querySelector('input[type="radio"]:checked');
        var rt = cr && cr.getAttribute('data-radio-type');
        showHint = rt === 'selling_plan';
      }
      hint.classList.toggle('subscription-plans-by-customer--hidden', !showHint);
      hint.hidden = !showHint;
      if (showHint) {
        hint.removeAttribute('aria-hidden');
        hint.removeAttribute('hidden');
      } else {
        hint.setAttribute('aria-hidden', 'true');
        hint.setAttribute('hidden', '');
      }
    }
  }

  function normalizeSellingPlanId(raw) {
    var s = String(raw || '').trim();
    if (!s) return '';
    if (/^\d+$/.test(s)) return s;
    var m = s.match(/(\d+)\s*$/);
    return m ? m[1] : s;
  }

  function readSellingPlanRawFromFieldset(fieldset) {
    if (!fieldset) return '';
    var checked = fieldset.querySelector('input[type="radio"]:checked');
    if (!checked || checked.disabled) return '';
    var rtype = checked.getAttribute('data-radio-type');
    if (rtype === 'one_time_purchase') return '';
    var raw =
      checked.getAttribute('data-selling-plan-id') ||
      (checked.dataset && checked.dataset.sellingPlanId) ||
      checked.value ||
      '';
    return String(raw).trim();
  }

  function readCheckedSellingPlanRaw(refs) {
    refs = refs || getLiveRefs();
    if (!refs.container) return '';
    /**
     * Always key off the *current* variant’s fieldset — never the first visible one in DOM order.
     * Otherwise a stale or still-open sibling fieldset (Vue lag, swatches, Markets PDP) can leave
     * a subscription radio checked for variant A while the form adds variant B; fetch/XHR hooks would
     * then attach that selling_plan to the wrong line.
     */
    var vid = getCurrentVariantId(refs);
    var fieldset = null;
    if (vid != null && !isNaN(vid)) {
      fieldset = refs.container.querySelector(
        '.subscription-plans-fieldset[data-variant-id="' + String(vid) + '"]'
      );
    }
    if (!fieldset || fieldsetSubscriptionInactive(fieldset)) {
      return '';
    }
    return readSellingPlanRawFromFieldset(fieldset);
  }

  function updateSubscribeButtonState(refs) {
    refs = refs || getLiveRefs();
    if (!refs.container) return;
    refs.container.querySelectorAll('.subscription-plans-by-customer__subscribe-btn').forEach(function(btn) {
      if (btn.classList.contains('subscription-plans-by-customer__subscribe-btn--busy')) {
        return;
      }
      var fs = btn.closest('.subscription-plans-fieldset');
      if (fieldsetSubscriptionInactive(fs)) {
        btn.classList.remove('subscription-plans-by-customer__subscribe-btn--busy');
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        return;
      }
      var raw = readSellingPlanRawFromFieldset(fs);
      var normalized = normalizeSellingPlanId(raw);
      var ok = !!normalized;
      btn.disabled = !ok;
      btn.setAttribute('aria-disabled', ok ? 'false' : 'true');
    });
  }

  function syncSellingPlan() {
    var refs = getLiveRefs();
    if (!refs.container || !refs.sellingPlanInput) return;
    var raw = readCheckedSellingPlanRaw(refs);
    var normalized = normalizeSellingPlanId(raw);
    if (refs.sellingPlanInput.value !== normalized) {
      refs.sellingPlanInput.value = normalized;
    }
    updateSubscribeButtonState(refs);
  }

  /** Debounced: Vue + our listeners can emit many change/MO events per tick — avoid main-thread freeze. */
  var _scheduleSyncTimer = null;
  function scheduleSyncSellingPlan() {
    if (_scheduleSyncTimer) clearTimeout(_scheduleSyncTimer);
    _scheduleSyncTimer = setTimeout(function() {
      _scheduleSyncTimer = null;
      syncSellingPlan();
      try {
        requestAnimationFrame(function() {
          syncSellingPlan();
        });
      } catch (eRaf) {}
    }, 45);
  }

  /** Use before ATC / submit when you need an immediate read — not for high-frequency listeners. */
  function flushSyncSellingPlan() {
    if (_scheduleSyncTimer) {
      clearTimeout(_scheduleSyncTimer);
      _scheduleSyncTimer = null;
    }
    syncSellingPlan();
    try {
      requestAnimationFrame(function() {
        syncSellingPlan();
      });
    } catch (eRaf2) {}
  }

  function getFormQuantity(refs) {
    refs = refs || getLiveRefs();
    if (!refs.form) return 1;
    var inputs = refs.form.querySelectorAll('input[name="quantity"]');
    var best = null;
    for (var qi = 0; qi < inputs.length; qi++) {
      var el = inputs[qi];
      var r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        best = el;
        break;
      }
    }
    if (!best && inputs.length) best = inputs[0];
    var q = parseInt(best && best.value, 10);
    return !isNaN(q) && q > 0 ? q : 1;
  }

  function clnfCartAddUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + 'cart/add.js';
  }

  function clnfOpenCartDrawerAfterAdd() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var cartUrl = root + 'cart.js';
    fetch(cartUrl, { credentials: 'same-origin' })
      .then(function(r) {
        return r.json();
      })
      .then(function(cart) {
        if (window.wetheme && typeof window.wetheme.toggleRightDrawer === 'function') {
          window.wetheme.toggleRightDrawer('cart', true, { cart: cart });
        }
        document.dispatchEvent(new CustomEvent('cart:build'));
      })
      .catch(function() {
        document.dispatchEvent(new CustomEvent('cart:build'));
      });
  }

  function hideDuplicatePlanContainers(refs) {
    var form = document.getElementById('AddToCartForm');
    var keep =
      refs && refs.container
        ? refs.container
        : form
          ? form.querySelector('.subscription-plans-by-customer')
          : null;
    document.querySelectorAll('.subscription-plans-by-customer').forEach(function(c) {
      if (!form || !form.contains(c)) {
        c.style.display = 'none';
        return;
      }
      if (keep && c !== keep) {
        c.style.display = 'none';
      }
    });
  }

  function connectMutationObserver(refs) {
    if (typeof MutationObserver === 'undefined') return;
    try {
      if (mo) {
        mo.disconnect();
        mo = null;
      }
      if (!refs.container) return;
      var moDebounce = null;
      mo = new MutationObserver(function() {
        if (moDebounce) clearTimeout(moDebounce);
        moDebounce = setTimeout(function() {
          moDebounce = null;
          scheduleSyncSellingPlan();
        }, 60);
      });
      mo.observe(refs.container, { subtree: true, attributes: true, attributeFilter: ['class'] });
    } catch (eMo) {}
  }

  /**
   * Vue adds product-form--ready after initial variant/bind sync; syncing fieldset visibility before that
   * can read an empty select. Re-run init once when the class appears.
   */
  function clnfWatchProductFormReadyOnce() {
    var el =
      document.querySelector('wetheme-product-form') ||
      document.querySelector('.product-form-vue');
    if (!el) {
      return;
    }
    if (el.classList.contains('product-form--ready')) {
      clnfInitSubscriptionPlansByCustomer();
      return;
    }
    if (typeof MutationObserver === 'undefined') {
      return;
    }
    var mo = new MutationObserver(function() {
      if (el.classList.contains('product-form--ready')) {
        try {
          mo.disconnect();
        } catch (eD) {}
        clnfInitSubscriptionPlansByCustomer();
      }
    });
    try {
      mo.observe(el, { attributes: true, attributeFilter: ['class'] });
    } catch (eO) {}
  }

  function clnfInitSubscriptionPlansByCustomer() {
    var refs = getLiveRefs();
    hideDuplicatePlanContainers(refs);

    /* Variant may be Vue-driven before select[name=id] exists; do not require variantInput for fieldset/hint sync. */
    if (!refs.container || !refs.sellingPlanInput || !refs.form) {
      window.__clnfSyncSubscriptionSellingPlanInput = function() {};
      return;
    }

    connectMutationObserver(refs);

    queueShowVariantPlans();

    window.__clnfSyncSubscriptionSellingPlanInput = function() {
      var r = getLiveRefs();
      showVariantPlans(getCurrentVariantId(r), r);
      syncSellingPlan();
    };

    installClnfSellingPlanCartHooks();
  }

  window.clnfReinitSubscriptionPlansByCustomer = clnfInitSubscriptionPlansByCustomer;

  /* ---- One-time document delegation (works after Vue swaps nodes) ---- */
  if (!window.__clnfSubscriptionPlansDocBound) {
    window.__clnfSubscriptionPlansDocBound = true;

    document.addEventListener(
      'click',
      function(ev) {
        var subscribeBtn =
          ev.target && ev.target.closest && ev.target.closest('.subscription-plans-by-customer__subscribe-btn');
        if (!subscribeBtn || !subscribeBtn.closest('#AddToCartForm')) return;
        var refs = getLiveRefs();
        if (!refs.container || !refs.container.contains(subscribeBtn)) return;
        ev.preventDefault();

        showVariantPlans(getCurrentVariantId(refs), refs);
        scheduleSyncSellingPlan();

        var fs = subscribeBtn.closest('.subscription-plans-fieldset');
        if (fieldsetSubscriptionInactive(fs)) return;

        var raw = readSellingPlanRawFromFieldset(fs);
        var planStr = normalizeSellingPlanId(raw);
        var planNum = parseInt(planStr, 10);
        if (!planStr || isNaN(planNum)) return;

        var vid = parseInt(fs.getAttribute('data-variant-id'), 10);
        if (isNaN(vid)) vid = getCurrentVariantId(refs);
        if (vid == null || isNaN(vid)) {
          window.alert('Please select a product option before subscribing.');
          return;
        }

        var qty = getFormQuantity(refs);
        var originalLabel = subscribeBtn.textContent;
        subscribeBtn.classList.add('subscription-plans-by-customer__subscribe-btn--busy');
        subscribeBtn.disabled = true;
        subscribeBtn.textContent = '…';

        fetch(clnfCartAddUrl(), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            items: [{ id: vid, quantity: qty, selling_plan: planNum }]
          })
        })
          .then(function(res) {
            if (!res.ok)
              return res.json().then(function(err) {
                throw err;
              });
            return res.json();
          })
          .then(function(data) {
            subscribeBtn.textContent = 'Added';
            setTimeout(function() {
              subscribeBtn.classList.remove('subscription-plans-by-customer__subscribe-btn--busy');
              subscribeBtn.textContent = originalLabel;
              updateSubscribeButtonState();
            }, 1800);

            var line = data && data.items && data.items[0] ? data.items[0] : data;
            var lineVariant = line && (line.variant_id || line.id);
            if (window.trackAddToCart && line && lineVariant) {
              try {
                var trackedQty = (line.quantity != null ? line.quantity : qty) || qty;
                window.trackAddToCart(lineVariant, trackedQty, {
                  product_id: line.product_id,
                  product_title: line.product_title || line.title,
                  variant_title: line.variant_title,
                  price: (line.price || 0) / 100,
                  value: ((line.price || 0) * trackedQty) / 100,
                  currency:
                    (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD'
                });
              } catch (eT) {}
            }
            clnfOpenCartDrawerAfterAdd();
          })
          .catch(function(err) {
            subscribeBtn.classList.remove('subscription-plans-by-customer__subscribe-btn--busy');
            subscribeBtn.textContent = originalLabel;
            updateSubscribeButtonState();
            var msg =
              (err && (err.description || err.message)) ||
              'Could not add subscription. Please try again.';
            window.alert(msg);
          });
      },
      true
    );

    document.addEventListener(
      'click',
      function(e) {
        var form = document.getElementById('AddToCartForm');
        if (!form || !form.contains(e.target)) return;
        var t = e.target;
        if (!t.closest || !t.closest('.subscription-plans-by-customer')) return;
        if (
          (t.matches && t.matches('input[type="radio"]')) ||
          (t.matches && t.matches('label')) ||
          (t.closest && t.closest('label'))
        ) {
          scheduleSyncSellingPlan();
        }
      },
      true
    );

    document.addEventListener(
      'change',
      function(e) {
        if (!e.target) return;
        var form = document.getElementById('AddToCartForm');
        if (!form || !form.contains(e.target)) return;
        if (e.target.closest && e.target.closest('.subscription-plans-by-customer')) {
          scheduleSyncSellingPlan();
          return;
        }
        queueShowVariantPlans();
      },
      false
    );

    document.addEventListener(
      'click',
      function(e) {
        var form = document.getElementById('AddToCartForm');
        if (!form || !e.target || !form.contains(e.target)) return;
        if (e.target.closest && e.target.closest('.swatch-element')) {
          queueShowVariantPlans();
        }
      },
      true
    );

    document.addEventListener(
      'input',
      function(e) {
        if (!e.target || !e.target.closest) return;
        if (e.target.closest('.subscription-plans-by-customer') && e.target.type === 'radio') {
          scheduleSyncSellingPlan();
        }
      },
      true
    );

    document.addEventListener('variant:change', function(e) {
      if (e.detail && e.detail.variant) {
        queueShowVariantPlans();
      }
    });

    document.addEventListener(
      'submit',
      function(e) {
        var form = e.target;
        if (!form || form.id !== 'AddToCartForm') return;
        showVariantPlans(getCurrentVariantId(), getLiveRefs());
        flushSyncSellingPlan();
      },
      true
    );

    var atcSelector =
      '.js-product-atc-btn, button[name="add"], button#AddToCart--main, input[name="add"][type="submit"]';

    function syncBeforeAtc(e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var btn = t.closest(atcSelector);
      var form = document.getElementById('AddToCartForm');
      if (!btn || !form || !form.contains(btn)) return;
      showVariantPlans(getCurrentVariantId(), getLiveRefs());
      flushSyncSellingPlan();
    }

    document.addEventListener('pointerdown', syncBeforeAtc, true);
    document.addEventListener('mousedown', syncBeforeAtc, true);
    document.addEventListener('touchstart', syncBeforeAtc, { capture: true, passive: true });
    document.addEventListener('click', syncBeforeAtc, true);
  }

  var hasRoot =
    document.querySelector('.subscription-plans-by-customer') &&
    document.getElementById('AddToCartForm');
  if (!hasRoot) {
    window.__clnfSyncSubscriptionSellingPlanInput = function() {};
    installClnfSellingPlanCartHooks();
  } else {
    clnfInitSubscriptionPlansByCustomer();
    clnfWatchProductFormReadyOnce();
  }

  function reinitWhenVueReady() {
    var wf = document.querySelector('wetheme-product-form');
    var mount = document.querySelector('.product-form-vue');
    if ((wf && wf.__vue__) || (mount && mount.__vue__)) {
      clnfInitSubscriptionPlansByCustomer();
    }
  }
  [0, 50, 120, 300, 700, 1600].forEach(function(ms) {
    setTimeout(reinitWhenVueReady, ms);
  });
  document.addEventListener('shopify:section:load', function() {
    setTimeout(reinitWhenVueReady, 80);
    setTimeout(reinitWhenVueReady, 400);
  });
})();

function installClnfSellingPlanCartHooks() {
  if (window.__clnfSellingPlanCartHooksInstalled) return;
  window.__clnfSellingPlanCartHooksInstalled = true;

  function ensurePlanSynced() {
    if (typeof window.__clnfSyncSubscriptionSellingPlanInput === 'function') {
      window.__clnfSyncSubscriptionSellingPlanInput();
    }
  }

  function normalizeSellingPlanId(raw) {
    var s = String(raw || '').trim();
    if (!s) return { str: '', num: NaN };
    if (/^\d+$/.test(s)) {
      var n = parseInt(s, 10);
      return { str: s, num: n };
    }
    var m = s.match(/(\d+)\s*$/);
    if (m) {
      n = parseInt(m[1], 10);
      return { str: m[1], num: n };
    }
    return { str: s, num: NaN };
  }

  function readPlanFromDom() {
    ensurePlanSynced();
    var el = document.querySelector('#AddToCartForm input.selected-selling-plan-id[name="selling_plan"]');
    if (!el || !el.value) return { str: '', num: NaN };
    return normalizeSellingPlanId(el.value);
  }

  function mergeStringBody(headers, body, planStr, planNum) {
    var h = new Headers(headers || {});
    var ct = (h.get('content-type') || '').toLowerCase();
    var looksJson = ct.indexOf('application/json') !== -1;
    if (!looksJson) {
      var t = body.trim();
      if (t.charAt(0) === '{' || t.charAt(0) === '[') {
        try {
          JSON.parse(t);
          looksJson = true;
          if (!h.get('content-type')) h.set('content-type', 'application/json');
        } catch (eJ) {}
      }
    }
    if (looksJson) {
      var data = JSON.parse(body);
      if (data.items && Array.isArray(data.items)) {
        for (var i = 0; i < data.items.length; i++) {
          if (data.items[i] && !('selling_plan' in data.items[i]) && !isNaN(planNum)) {
            data.items[i].selling_plan = planNum;
          }
        }
      } else if (data.id && !('selling_plan' in data) && !isNaN(planNum)) {
        data.selling_plan = planNum;
      }
      return { body: JSON.stringify(data), headers: h };
    }
    if (ct.indexOf('application/x-www-form-urlencoded') !== -1 || (!looksJson && body.indexOf('=') !== -1)) {
      var params = new URLSearchParams(body);
      if (!params.get('selling_plan') && planStr) params.set('selling_plan', planStr);
      return { body: params.toString(), headers: h };
    }
    return { body: body, headers: h };
  }

  function mergeFetchInit(url, init) {
    try {
      var path = typeof url === 'string' ? url : url && url.url ? url.url : '';
      if (!path || path.indexOf('cart/add') === -1) return [url, init];

      var plan = readPlanFromDom();
      if (!plan.str || isNaN(plan.num)) return [url, init];

      init = init ? Object.assign({}, init) : {};
      var body = init.body;
      if (body == null) return [url, init];

      var headers = new Headers(init.headers || {});

      if (typeof body === 'string') {
        var merged = mergeStringBody(headers, body, plan.str, plan.num);
        init.body = merged.body;
        init.headers = merged.headers;
      } else if (typeof FormData !== 'undefined' && body instanceof FormData) {
        if (!body.get('selling_plan')) body.set('selling_plan', plan.str);
        init.headers = headers;
      } else if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        if (!body.get('selling_plan')) body.set('selling_plan', plan.str);
        init.headers = headers;
      }
    } catch (e) {}
    return [url, init];
  }

  function wrapFetch() {
    // Guard: scheduleFetchRetries fires this 5–8 times; without a check each call stacks
    // another closure on window.fetch, causing mergeFetchInit to run once per layer on every
    // cart/add request (needless JSON parse/serialize on every add-to-cart).
    if (window.fetch && window.fetch.__clnfWrapped) return;
    var inner = window.fetch;
    function outer(input, init) {
      var m = mergeFetchInit(input, init);
      return inner.call(this, m[0], m[1]);
    }
    outer.__clnfWrapped = true;
    window.fetch = outer;
  }

  function scheduleFetchRetries() {
    var delays = [0, 30, 100, 250, 600];
    delays.forEach(function(ms) {
      setTimeout(wrapFetch, ms);
    });
    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        function() {
          [0, 50, 200].forEach(function(ms) {
            setTimeout(wrapFetch, ms);
          });
        },
        { once: true }
      );
    }
  }

  scheduleFetchRetries();

  (function patchXHR() {
    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function(method, url) {
      this.__clnfUrl = typeof url === 'string' ? url : '';
      this.__clnfMethod = method;
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function(body) {
      try {
        var url = this.__clnfUrl || '';
        if (
          url.indexOf('cart/add') === -1 ||
          String(this.__clnfMethod || '').toUpperCase() !== 'POST' ||
          body == null
        ) {
          return origSend.call(this, body);
        }
        var plan = readPlanFromDom();
        if (!plan.str || isNaN(plan.num)) return origSend.call(this, body);
        if (typeof body === 'string') {
          var t = body.trim();
          if (t.charAt(0) === '{' || t.charAt(0) === '[') {
            try {
              var data = JSON.parse(t);
              if (data.items && Array.isArray(data.items)) {
                for (var i = 0; i < data.items.length; i++) {
                  if (data.items[i] && !('selling_plan' in data.items[i])) {
                    data.items[i].selling_plan = plan.num;
                  }
                }
              } else if (data.id && !('selling_plan' in data)) {
                data.selling_plan = plan.num;
              }
              body = JSON.stringify(data);
            } catch (eJ) {}
          } else if (body.indexOf('=') !== -1) {
            var params = new URLSearchParams(body);
            if (!params.get('selling_plan')) params.set('selling_plan', plan.str);
            body = params.toString();
          }
        } else if (typeof FormData !== 'undefined' && body instanceof FormData && !body.get('selling_plan')) {
          body.set('selling_plan', plan.str);
        }
      } catch (e) {}
      return origSend.call(this, body);
    };
  })();
}
